import {
  Component,
  Host,
  h,
  State,
  Listen,
  EventEmitter,
  Event,
  Prop,
} from '@stencil/core';
import {
  SessionRepository,
  SessionService,
  SessionModel,
  QuoteService,
  QuoteModel,
  QuoteCreateBody,
  QuoteHelper,
  QuoteRepository,
  BasketService,
  SessionHelper,
  Language,
  BasketModel,
  RoomService,
  RoomModel,
  BasketRepository,
} from '@cesare';
import {
  first,
  switchMap,
  distinctUntilChanged,
  debounceTime,
  map,
} from 'rxjs';
import { QwInputEmitter } from '../shared/qw-input/qw-input';
import {
  GuestDetailFormProperty,
  QW_ROOM_CODE,
  QwSessionSettings,
} from '../../index';
import { emailIsValid } from '../../global/app';
import { of, combineLatest } from 'rxjs';
import { QwSelect } from '../shared/qw-select/qw-select';
import { getSessionSettingsFromSessionStorage } from '../../core/storage/storage.service';
import { isAccommodationSatisfied } from '../../core/basket/basket.service';
import { isEqual } from 'lodash-es';
import { loadingService } from '../../core/qw-loading-manager/qw-loading.service';

@Component({
  tag: 'qw-book',
  styleUrl: 'qw-book.scss',
  shadow: false,
})
export class QwBook {
  @Prop() guestDefaultPhoneCountry: string;
  @Prop() currentLang: string;
  @State() quote: QuoteModel;
  @State() rooms: RoomModel[];
  @State() isConfirmedConditions: boolean;
  @State() formQuote: QuoteCreateBody;
  @State() showFormErrors: boolean = false;
  @State() numberOfGuests: number;
  @State() numberOfNights: number;
  @State() isGuestAccordionOpen: boolean = false;
  @State() isConditionAccordionOpen: boolean = false;
  @State() basket: BasketModel;
  @State() isClickedConfirmConditions: boolean = false;
  @State() isClickedPromoSubscription: boolean = false;
  @State() sessionSettings: QwSessionSettings;
  @State() basketHasDorm: boolean = false;
  @State() componentLoading: boolean = true;
  @State() qwQuoteHasError: boolean = false;
  @State() renderTriggered: boolean = false;
  @Event() qwBookIsLoaded: EventEmitter<void>;
  @Event() qwBookQuote: EventEmitter<QuoteModel>;
  @Event() qwBookFormQuoteChange: EventEmitter<QuoteCreateBody>;
  @Event() qwBookFormQuoteIsValid: EventEmitter<boolean>;

  private session: SessionModel;
  private mandatoriesCustomerFields = [
    GuestDetailFormProperty.FirstName,
    GuestDetailFormProperty.LastName,
    GuestDetailFormProperty.CountryCode,
    GuestDetailFormProperty.EmailAddress,
  ];

  public componentWillLoad() {
    this.formQuote = QuoteHelper.initObjectForCreateBody();
    this.formQuote.guestDetails = [];

    this.sessionSettings = getSessionSettingsFromSessionStorage();

    SessionService.getSession().pipe(distinctUntilChanged(isEqual)).subscribe();
    SessionRepository.sessionLoaded$
      .pipe(
        switchMap((session) => {
          this.session = session;
          return BasketService.getBasket(session);
        }),
        distinctUntilChanged(isEqual),
      )
      .subscribe();

    BasketRepository.basketWithPrice$
      .pipe(
        first(),
        switchMap((basket) => {
          this.basket = basket;
          // const numberOfAccommodations = BasketHelper.getNumberOfAccommodation(basket);
          this.numberOfGuests = SessionHelper.getTotalGuests(this.session);
          this.numberOfNights = SessionHelper.getNumberOfNights(this.session);
          if (
            isAccommodationSatisfied(this.basket.rooms, {
              session: this.session,
            })
          ) {
            return QuoteService.getQuote(this.session.sessionId);
          }
          this.quote = null;
          return of(null);
        }),
        distinctUntilChanged(isEqual),
      )
      .subscribe();

    QuoteRepository.quoteLoaded$
      .pipe(first(), distinctUntilChanged(isEqual))
      .subscribe(() => this.qwBookIsLoaded.emit());
    QuoteRepository.quoteLoaded$
      .pipe(distinctUntilChanged(isEqual))
      .subscribe((quote) => {
        this.quote = quote;
        if (this.session) {
          RoomService.getRooms(this.session.sessionId).subscribe((rooms) => {
            this.rooms = rooms;
            this.basketHasDorm = this.isDormInBasket();
          });
        }
        this.qwBookQuote.emit(this.quote);
        // @ts-ignore
        Array.from({ length: this.numberOfGuests }, (_, i) => i + 1).map(() => {
          this.formQuote.guestDetails = this.createGuestDetailInForm();
        });
      });

    this.componentIsLoading$.pipe(distinctUntilChanged()).subscribe((value) => {
      this.componentLoading = value;
      if (this.componentLoading === false) {
        this.qwQuoteHasError = this.quoteHasError();
      }
      this.setServiceLoading(this.componentLoading);
    });

    this.setServiceLoading(true);

    loadingService.loadComplete$.subscribe((res) => {
      this.renderTriggered = res;
    });
  }

  public componentIsLoading$ = combineLatest([
    SessionRepository.sessionIsLoading$,
    BasketRepository.basketIsLoading$,
    QuoteRepository.quoteLoaded$,
  ]).pipe(
    map(([sessionLoading, basketLoading, quoteLoaded]) => {
      return sessionLoading || basketLoading || quoteLoaded.loading;
    }),
    debounceTime(0),
    distinctUntilChanged(),
  );

  private setServiceLoading(isLoading: boolean) {
    loadingService.setLoading({
      idComponent: 'quote-checkout',
      isLoading,
    });
  }

  private quoteHasError() {
    return !Object.keys(this.quote).length;
  }

  @Listen('qwBookGuestDetailChangeForm')
  public qwBookGuestDetailChangeForm(e: CustomEvent<QuoteCreateBody>) {
    this.formQuote.customerDetails = e.detail.customerDetails;
    this.emitForm();
  }

  @Listen('qwInputChanged')
  public guestDetailInputChanged(e: CustomEvent<QwInputEmitter>) {
    const { name, value } = e.detail;
    if (this.isConfirmConditionsFormName(name)) {
      this.isConfirmedConditions = Boolean(value);
      this.emitForm();
    }
    if (this.isPromoSubscriptionFormName(name)) {
      this.formQuote.customerDetails.marketingConsent = {
        optinHotel: value as boolean,
        optinPartners: value as boolean,
        optinHotelAccepted: value as boolean,
        optinPartnersAccepted: value as boolean,
      };
      this.emitForm();
    }
  }

  @Listen('qwScrollToTaxesDetails', { target: 'window' })
  public scrollToDetails() {
    this.isConditionAccordionOpen = true;
    setTimeout(() => {
      const detailsTaxes = document.querySelector('.qw-book__terms');
      if (detailsTaxes) {
        const headerOffset = 0;
        const offsetPosition =
          detailsTaxes.getBoundingClientRect().top +
          window.scrollY -
          headerOffset;

        window.scrollTo({
          top: offsetPosition,
          behavior: 'smooth',
        });
      }
    }, 200);
  }

  @Listen('qwBasketChange', { target: 'window' })
  public onBasketChange(e: CustomEvent<BasketModel>) {
    // console.log('BASKET CHANGE', e.detail)
    this.basket = e.detail;
  }

  public isFormValid() {
    if (!this.formQuote) {
      return;
    }

    return (
      this.isConfirmedConditions &&
      this.hasAllMandatoryPropertiesSet() &&
      emailIsValid(this.formQuote.customerDetails.emailAddress)
    );
  }

  private hasAllMandatoryPropertiesSet() {
    const formQuoteWithPropertiesSet = Object.keys(
      this.formQuote.customerDetails,
    ).reduce((acc, key) => {
      return this.formQuote.customerDetails[key]
        ? { ...acc, [key]: this.formQuote.customerDetails[key] }
        : acc;
    }, {});

    const formQuoteWithPropertiesSetKeys = Object.keys(
      formQuoteWithPropertiesSet,
    );
    return this.mandatoriesCustomerFields.every((field) =>
      formQuoteWithPropertiesSetKeys.includes(field),
    );
  }

  @Listen('qwTextareaChanged')
  public specialRequestsChanged(e: CustomEvent<QwInputEmitter>) {
    this.formQuote.specialRequest = e.detail.value.toString();
    this.emitForm();
  }

  private isConfirmConditionsFormName(name: QwInputEmitter['name']) {
    return name === GuestDetailFormProperty.ConfirmConditions;
  }

  private isPromoSubscriptionFormName(name: QwInputEmitter['name']) {
    return name === GuestDetailFormProperty.PromoSubscription;
  }

  private emitForm() {
    this.qwBookFormQuoteChange.emit(this.formQuote);
    if (this.isFormValid()) {
      this.qwBookFormQuoteIsValid.emit(true);
    } else {
      this.qwBookFormQuoteIsValid.emit(false);
      this.showFormErrors = true;
    }
  }

  public showMissingRequiredForm() {
    return (
      this.showFormErrors &&
      (!this.isFormValid() || !this.isConfirmedConditions)
    );
  }

  private guestDetailInfoTitleSelectChanged(guestIndex: number, e) {
    this.formQuote.guestDetails[guestIndex].title = e.target.value;
    this.emitForm();
  }

  private guestDetailInfoTypeSelectChanged(guestIndex: number, e) {
    this.formQuote.guestDetails[guestIndex].type = e.target.value;
    this.emitForm();
  }

  private guestDetailInfoFirstNameSelectChanged(guestIndex: number, e) {
    this.formQuote.guestDetails[guestIndex].firstName = e.target.value;
    this.emitForm();
  }

  private guestDetailInfoLastNameSelectChanged(guestIndex: number, e) {
    this.formQuote.guestDetails[guestIndex].lastName = e.target.value;
    this.emitForm();
  }

  private guestDetailInfoCheckInTimeSelectChanged(e) {
    this.formQuote.customerDetails.arrivalTime = e.target.value;
    this.emitForm();
  }

  private createGuestDetailInForm() {
    return [
      ...this.formQuote.guestDetails,
      {
        title: undefined,
        type: 'Adult',
        firstName: undefined,
        lastName: undefined,
      },
    ];
  }

  public isDormInBasket() {
    const roomsfound = this.basket?.rooms.reduce((acc, basketRoom) => {
      return [
        ...acc,
        this.rooms.find((r) => r.roomId === basketRoom.roomId).code,
      ];
    }, []);
    return !!roomsfound.find((code) => code.includes(QW_ROOM_CODE.Bunk));
  }

  render() {
    const dataExtraSkeleton = [
      {
        qwRepeat: 1,
        qwWidth: '40%',
        qwHeight: '24px',
        qwType: 'title',
      },
      {
        qwRepeat: 1,
        qwWidth: '100%',
        qwHeight: '40px',
        qwType: 'text',
      },
    ];
    const dataExtraSkeleton1 = [
      {
        qwRepeat: 1,
        qwWidth: '60%',
        qwHeight: '28px',
        qwType: 'title',
      },
    ];

    return (
      <Host
        class={
          // `${!this.quote ? 'qw-book--loading' : 'qw-book--loaded'}`
          `${this.componentLoading ? 'loading-skeleton' : ''}
        ${this.renderTriggered ? 'visualize-quote' : ''}`
        }
      >
        <div class="book-skeleton">
          {/* <qw-skeleton class="qw-skeleton-title" qwData={dataExtraSkeleton1}></qw-skeleton> */}
          <div class="skeleton-card-q">
            <qw-skeleton
              class="qw-skeleton-quote"
              qwData={dataExtraSkeleton}
            ></qw-skeleton>
            <qw-skeleton
              class="qw-skeleton-quote"
              qwData={dataExtraSkeleton}
            ></qw-skeleton>
            <qw-skeleton
              class="qw-skeleton-quote"
              qwData={dataExtraSkeleton}
            ></qw-skeleton>
            <qw-skeleton
              class="qw-skeleton-quote"
              qwData={dataExtraSkeleton}
            ></qw-skeleton>
            <qw-skeleton
              class="qw-skeleton-quote"
              qwData={dataExtraSkeleton}
            ></qw-skeleton>
            <qw-skeleton
              class="qw-skeleton-quote"
              qwData={dataExtraSkeleton}
            ></qw-skeleton>
          </div>
          <div class="skeleton-card-q">
            <qw-skeleton
              class="qw-skeleton-quote skeleton-title"
              qwData={dataExtraSkeleton1}
            ></qw-skeleton>
          </div>
          <div class="skeleton-card-q">
            <qw-skeleton
              class="qw-skeleton-quote skeleton-title"
              qwData={dataExtraSkeleton1}
            ></qw-skeleton>
          </div>
        </div>
        {!this.componentLoading &&
          !this.quote &&
          this.basket?.rooms?.length === 0 && (
            <p class="qw-book__warning">
              Before going to checkout, please add rooms to basket.
            </p>
          )}
        {!this.componentLoading &&
        !this.qwQuoteHasError &&
        this.basket?.rooms?.length > 0 ? (
          <div class="qw-book__wrapper">
            {/* <h3>{Language.getTranslation('checkoutInfo')}</h3> */}
            <p class="qw-book__mandatory-info">
              (*) {Language.getTranslation('mandatoryFields')}
            </p>
            <div class="qw-book__customer-details-wrapper">
              {this.componentLoading && <div class="qw-loader"></div>}
              <qw-book-guest-detail
                qwBookGuestDetailDefaultPhoneCountry={
                  this.guestDefaultPhoneCountry
                }
                qwBookFormShowError={this.showFormErrors}
                qwBookGuestDetailTitleOptions={
                  this.quote && this.quote.guestTitles
                }
              />
            </div>

            <div class="qw-book__guest-details">
              {this.componentLoading && <div class="qw-loader"></div>}
              <div
                class="qw-book__heading"
                onClick={() =>
                  (this.isGuestAccordionOpen = !this.isGuestAccordionOpen)
                }
              >
                <h3>{Language.getTranslation('saveTimeAtCheckIn')}</h3>
                <span
                  class={`qw-book__arrow ${
                    this.isGuestAccordionOpen ? 'qw-book__arrow--rotate' : ''
                  }`}
                />
              </div>
              {!this.componentLoading && this.isGuestAccordionOpen && (
                <div class="qw-book__content">
                  {Array.from(Array(this.numberOfGuests).keys()).map(
                    (guestIndex: number) => {
                      return (
                        <div class="qw-book__guest-detail">
                          {this.componentLoading && (
                            <div class="qw-loader"></div>
                          )}
                          <h3>
                            {Language.getTranslation('guest')} {guestIndex + 1}
                          </h3>
                          <div class="qw-book__guest-detail--info">
                            <div class="qw-book--form-row">
                              <QwSelect
                                QwSelectLabel={Language.getTranslation('title')}
                                QwSelectName={GuestDetailFormProperty.Title}
                                QwSelectOnChange={(e) =>
                                  this.guestDetailInfoTitleSelectChanged(
                                    guestIndex,
                                    e,
                                  )
                                }
                              >
                                <option value="">--</option>
                                {this.quote &&
                                  this.quote.guestTitles.map((title) => (
                                    <option value={title.value}>
                                      {title.text}
                                    </option>
                                  ))}
                              </QwSelect>
                              {!this.basketHasDorm && (
                                <QwSelect
                                  QwSelectLabel={Language.getTranslation(
                                    'typeOf',
                                  )}
                                  QwSelectName="Type"
                                  QwSelectOnChange={(e) =>
                                    this.guestDetailInfoTypeSelectChanged(
                                      guestIndex,
                                      e,
                                    )
                                  }
                                >
                                  <option value="Adult">
                                    {Language.getTranslation('over18')}
                                  </option>
                                  <option value="Child">
                                    {Language.getTranslation('under18')}
                                  </option>
                                </QwSelect>
                              )}
                            </div>
                            <div class="qw-book--form-row">
                              <label htmlFor="guestDetailFirstName">
                                {Language.getTranslation('firstName')}
                                <input
                                  type="text"
                                  id="guestDetailFirstName"
                                  name="guestDetailFirstName"
                                  onChange={(e) =>
                                    this.guestDetailInfoFirstNameSelectChanged(
                                      guestIndex,
                                      e,
                                    )
                                  }
                                />
                              </label>
                              <label htmlFor="guestDetailLastName">
                                {Language.getTranslation('lastName')}
                                <input
                                  type="text"
                                  id="guestDetailLastName"
                                  name="guestDetailLastName"
                                  onChange={(e) =>
                                    this.guestDetailInfoLastNameSelectChanged(
                                      guestIndex,
                                      e,
                                    )
                                  }
                                />
                              </label>
                            </div>
                          </div>
                        </div>
                      );
                    },
                  )}
                  <div class="qw-book__other-info">
                    <h3>{Language.getTranslation('otherInfo')}</h3>
                    <div class="qw-book__other-info-content">
                      <QwSelect
                        QwSelectLabel={Language.getTranslation('checkInTime')}
                        QwSelectName="checkInTime"
                        QwSelectOnChange={(e) =>
                          this.guestDetailInfoCheckInTimeSelectChanged(e)
                        }
                      >
                        <option value="--">--</option>
                        <option value="10:00">10:00</option>
                        <option value="11:00">11:00</option>
                        <option value="12:00">12:00</option>
                        <option value="10:30">13:00</option>
                        <option value="11:30">14:00</option>
                        <option value="12:00">15:00</option>
                        <option value="12:00">16:00</option>
                        <option value="12:00">18:00</option>
                        <option value="12:00">19:00</option>
                        <option value="12:00">20:00</option>
                        <option value="12:00">21:00</option>
                      </QwSelect>
                      <div class="qw-book__special-requests">
                        <div class="qw-book__special-requests__caption">
                          {Language.getTranslation('specialRequest')}
                        </div>
                        <div class="qw-book__special-requests__content">
                          <qw-textarea qwTextareaName="specialRequest" />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>

            <div class="qw-book__terms">
              {this.componentLoading && <div class="qw-loader"></div>}
              <div
                class="qw-book__heading"
                onClick={() =>
                  (this.isConditionAccordionOpen =
                    !this.isConditionAccordionOpen)
                }
              >
                <h3>{Language.getTranslation('specificSalesConditions')}</h3>
                <span
                  class={`qw-book__arrow ${
                    this.isConditionAccordionOpen
                      ? 'qw-book__arrow--rotate'
                      : ''
                  }`}
                />
              </div>
              {!this.componentLoading && this.isConditionAccordionOpen && (
                <div class="qw-book__content">
                  {this.quote && (
                    <qw-book-condition
                      guestsNum={this.numberOfGuests}
                      nightsNum={this.numberOfNights}
                    />
                  )}
                </div>
              )}
            </div>

            <div class="qw-book__confirmation">
              <div class={`qw-book__confirmation-checkbox`}>
                <qw-input
                  qwInputType="checkbox"
                  qwInputName="promoSubscription"
                />
                <div
                  class="label__promoSubscription"
                  onClick={() =>
                    (this.isClickedPromoSubscription =
                      !this.isClickedPromoSubscription)
                      ? this.guestDetailInputChanged({
                          detail: {
                            name: 'promoSubscription',
                            value: true,
                          },
                        } as CustomEvent)
                      : this.guestDetailInputChanged({
                          detail: {
                            name: 'promoSubscription',
                            value: false,
                          },
                        } as CustomEvent)
                  }
                >
                  {Language.getTranslation('informationRequest')}
                </div>
              </div>
              <div
                class={`qw-book__confirmation-checkbox ${
                  this.showFormErrors && !this.isConfirmedConditions
                    ? 'qw-book__confirmation-checkbox--error'
                    : ''
                }`}
              >
                <qw-input
                  qwInputType="checkbox"
                  qwInputName="confirmConditions"
                />
                <div
                  class="label__confirmConditions"
                  onClick={() =>
                    (this.isClickedConfirmConditions =
                      !this.isClickedConfirmConditions)
                      ? this.guestDetailInputChanged({
                          detail: {
                            name: 'confirmConditions',
                            value: true,
                          },
                        } as CustomEvent)
                      : this.guestDetailInputChanged({
                          detail: {
                            name: 'confirmConditions',
                            value: false,
                          },
                        } as CustomEvent)
                  }
                >
                  {Language.getTranslation('termsAndConditionMessage')} *
                </div>
              </div>
            </div>

            <div class="qw-book__pay">
              {this.showMissingRequiredForm() ? (
                <span class="qw-book__missing-required-fields">
                  {Language.getTranslation('missingRequiredFields')}
                </span>
              ) : (
                ''
              )}
            </div>
          </div>
        ) : (
          <div class="qw-book__error-quote">
            {Language.getTranslation('quoteErrorMessage')}
          </div>
        )}
      </Host>
    );
  }
}
